/**
 * Notification Badge Component
 *
 * Icono de campana con badge que muestra notificaciones no leídas
 */

import React, { useState } from "react";
import {
  IconButton,
  Badge,
  Tooltip,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  Chip,
  alpha
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  CheckCircleOutline,
  InfoOutlined,
  WarningAmberOutlined,
  ErrorOutline,
  Close as CloseIcon
} from "@mui/icons-material";
import { useNotifications } from "../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";

// Función para parsear correctamente las fechas de SQL Server (UTC)
const parseNotificationDate = (dateString) => {
  if (!dateString) return new Date();

  console.log("📅 Parsing notification date:", {
    original: dateString,
    type: typeof dateString,
    now: new Date().toISOString()
  });

  // Si ya es un objeto Date, devolverlo
  if (dateString instanceof Date) {
    return dateString;
  }

  // Convertir a string si no lo es
  const dateStr = String(dateString);

  // Si la fecha ya tiene 'Z' al final (UTC), usarla directamente
  if (dateStr.endsWith('Z')) {
    const parsed = new Date(dateStr);
    console.log("📅 Parsed with Z (UTC):", parsed.toISOString());
    return parsed;
  }

  // Si no tiene 'Z', asumir que es UTC (GETUTCDATE() en SQL Server)
  // Agregar 'Z' para indicar que es UTC
  const isoDate = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  const parsed = new Date(isoDate + 'Z');
  console.log("📅 Parsed as UTC:", {
    input: isoDate,
    outputUTC: parsed.toISOString(),
    outputLocal: parsed.toString()
  });
  return parsed;
};

const NotificationBadge = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    // Marcar como leída si no lo está
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Navegar a la entidad relacionada si existe
    if (notification.entityType && notification.entityId) {
      switch (notification.entityType) {
        case "solicitudes":
          navigate(`/solicitudes/${notification.entityId}`);
          break;
        case "directores":
          // Aquí entityId sería nroFianza, necesitamos nroSolicitud
          // Por simplicidad, navegar a la lista de directores
          navigate(`/directores`);
          break;
        case "liquidaciones":
          navigate(`/intermediarios/liquidaciones`);
          break;
        case "backlog":
          navigate(`/backlog`);
          break;
        default:
          break;
      }
    }

    handleClose();
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation(); // Evitar que se dispare el click de la notificación
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "notifications-popover" : undefined;

  // Obtener icono según tipo de notificación
  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: "small" };
    switch (type) {
      case "success":
        return <CheckCircleOutline {...iconProps} sx={{ color: "#2e7d32" }} />;
      case "info":
        return <InfoOutlined {...iconProps} sx={{ color: "#1976d2" }} />;
      case "warning":
        return <WarningAmberOutlined {...iconProps} sx={{ color: "#ed6c02" }} />;
      case "error":
        return <ErrorOutline {...iconProps} sx={{ color: "#d32f2f" }} />;
      default:
        return <InfoOutlined {...iconProps} sx={{ color: "#1976d2" }} />;
    }
  };

  // Obtener color de chip según tipo
  const getChipColor = (type) => {
    switch (type) {
      case "success":
        return { color: "#2e7d32", bg: alpha("#2e7d32", 0.1) };
      case "info":
        return { color: "#1976d2", bg: alpha("#1976d2", 0.1) };
      case "warning":
        return { color: "#ed6c02", bg: alpha("#ed6c02", 0.1) };
      case "error":
        return { color: "#d32f2f", bg: alpha("#d32f2f", 0.1) };
      default:
        return { color: "#1976d2", bg: alpha("#1976d2", 0.1) };
    }
  };

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: alpha("#fff", 0.1)
            }
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? (
              <NotificationsIcon />
            ) : (
              <NotificationsNoneIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 600,
            mt: 1.5,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Notificaciones
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              sx={{ textTransform: "none" }}
            >
              Marcar todas como leídas
            </Button>
          )}
        </Box>

        {/* Lista de notificaciones */}
        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <NotificationsNoneIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography color="text.secondary">
              No tienes notificaciones
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 450, overflow: "auto" }}>
            {notifications.map((notification, index) => {
              const chipStyle = getChipColor(notification.type);
              return (
                <React.Fragment key={notification.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      backgroundColor: notification.isRead
                        ? "transparent"
                        : alpha("#1976d2", 0.08),
                      "&:hover": {
                        backgroundColor: notification.isRead
                          ? alpha("#000", 0.04)
                          : alpha("#1976d2", 0.12)
                      },
                      position: "relative"
                    }}
                  >
                    <Box
                      sx={{
                        mr: 2,
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 0.5
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={notification.isRead ? 500 : 700}
                            sx={{ flex: 1, pr: 5 }}
                          >
                            {notification.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.875rem" }}>
                            {notification.message}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1
                            }}
                          >
                            <Chip
                              label={notification.type}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: chipStyle.color,
                                backgroundColor: chipStyle.bg,
                                textTransform: "uppercase"
                              }}
                            />
                            <Box component="span" sx={{ fontSize: "0.75rem", color: "text.disabled" }}>
                              {formatDistanceToNow(
                                parseNotificationDate(notification.createdAt),
                                { addSuffix: true, locale: es }
                              )}
                            </Box>
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", alignItems: "center", gap: 0.5 }}>
                      {!notification.isRead && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#1976d2",
                            flexShrink: 0
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteNotification(e, notification.id)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            backgroundColor: alpha("#d32f2f", 0.1)
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBadge;
